import { useEffect, useState } from "react";
import { Button, Flex, Heading, Text } from "@radix-ui/themes";
import { WifiOff, ServerCrash } from "lucide-react";
import { ICON_SIZE, RefreshIcon } from "./actionIcons";

/**
 * Shown when the startup /getuserdata call cannot be answered at all.
 *
 * This screen exists because an installed PWA has no address bar: before it,
 * a launch with no signal alert()ed and then sat on a spinner forever, and
 * the only escape was force-quitting the app. So the two things it must
 * always offer are an explanation and a way back in.
 *
 * Distinguishes "your device is offline" from "we cannot reach the server",
 * because the first is something the user can act on and the second is not.
 */
export default function ConnectionError({ onRetry }) {
    const [online, setOnline] = useState(() => navigator.onLine);
    const [retrying, setRetrying] = useState(false);

    // The retry happens automatically when the connection returns (Cars.jsx
    // listens for 'online'), but the wording here should keep up regardless.
    useEffect(() => {
        const on = () => setOnline(true);
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, []);

    const handleRetry = () => {
        setRetrying(true);
        onRetry?.();
        // Purely cosmetic: a failed retry re-mounts this screen, and without a
        // moment of feedback the button would look like it did nothing.
        setTimeout(() => setRetrying(false), 1500);
    };

    const Icon = online ? ServerCrash : WifiOff;

    return (
        <Flex
            align="center"
            justify="center"
            direction="column"
            gap="3"
            style={{ minHeight: "100vh", padding: "1.5rem", textAlign: "center" }}
        >
            <Icon size={40} strokeWidth={1.5} style={{ color: "var(--theme-muted)" }} aria-hidden="true" />

            <Heading size="4" weight="medium">
                {online ? "Can't reach the server" : "You're offline"}
            </Heading>

            <Text size="2" color="gray" style={{ maxWidth: "28rem" }}>
                {online
                    ? "The app loaded, but the server didn't respond. It may be restarting — try again in a moment."
                    : "Check your mobile data or Wi-Fi. The app will reconnect on its own as soon as you're back online."}
            </Text>

            <Button variant="solid" onClick={handleRetry} disabled={retrying} style={{ marginTop: "0.5rem" }}>
                <RefreshIcon size={ICON_SIZE} />
                {retrying ? "Trying…" : "Try again"}
            </Button>
        </Flex>
    );
}
